/**
 * Operations Financial Page
 *
 * Dashboard for financial metrics including revenue, invoices, quotes, and projects.
 */

import {
  useFinancialSummary,
  usePaymentAging,
  useRevenueByType,
  useRevenueByPeriod,
  useTopAccounts,
  useTopProjects,
  useQuoteFunnel,
} from '../hooks/useFinancialMetrics';
import { RevenueOverviewCard } from '../components/RevenueOverviewCard';
import { InvoiceStatusCard } from '../components/InvoiceStatusCard';
import { PaymentAgingCard } from '../components/PaymentAgingCard';
import { QuoteConversionCard } from '../components/QuoteConversionCard';
import { RevenueByTypeChart } from '../components/RevenueByTypeChart';
import { RevenueTimelineChart } from '../components/RevenueTimelineChart';
import { TopAccountsTable } from '../components/TopAccountsTable';
import { TopProjectsTable } from '../components/TopProjectsTable';

export function FinancialPage() {
  const { data: summary, isLoading: summaryLoading } = useFinancialSummary();
  const { data: paymentAging, isLoading: agingLoading } = usePaymentAging();
  const { data: revenueByType, isLoading: typeLoading } = useRevenueByType();
  const { data: revenueByPeriod, isLoading: periodLoading } = useRevenueByPeriod(12);
  const { data: topAccounts, isLoading: accountsLoading } = useTopAccounts(5);
  const { data: topProjects, isLoading: projectsLoading } = useTopProjects(5);
  const { data: quoteFunnel, isLoading: funnelLoading } = useQuoteFunnel(6);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">Financial Overview</h1>
        <p className="text-sm text-muted-foreground">
          Revenue metrics, invoice tracking, and financial health
        </p>
      </div>

      {/* Top Row - Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <RevenueOverviewCard data={summary} loading={summaryLoading} />
        <InvoiceStatusCard data={summary?.invoices} loading={summaryLoading} />
        <PaymentAgingCard data={paymentAging} loading={agingLoading} />
        <QuoteConversionCard data={quoteFunnel} loading={funnelLoading} />
      </div>

      {/* Middle Row - Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RevenueTimelineChart data={revenueByPeriod} loading={periodLoading} />
        <RevenueByTypeChart data={revenueByType} loading={typeLoading} />
      </div>

      {/* Bottom Row - Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopAccountsTable data={topAccounts} loading={accountsLoading} />
        <TopProjectsTable data={topProjects} loading={projectsLoading} />
      </div>
    </div>
  );
}
